import * as feedApi from '@apis/feeds-v2';
import { ProductCollection } from '@models/productCollection';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button, Input, Switch, Form, Modal, ColorPicker, Checkbox } from 'antd';
import { Feed, FeedData } from '@models/feed';
import FileUploadButton from '@components/common/FileUploadButton';
import { cdnImageUrl, resizeImage } from '@utils/image';
import { extractFileName } from '@utils/string';
import { RcFile } from 'antd/es/upload';
import SelectProductCollectionModal from './SelectProductCollectionModal';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { CaretDownOutlined, CaretUpOutlined, DeleteOutlined, DragOutlined, EditOutlined } from '@ant-design/icons';
import { removeTempProperties } from '@utils/dataTransform';

const ALL_SECTION_TYPE = '전체';

interface SortableSectionTypeItemProps {
  sectionType: string;
  itemCount: number;
  id: string;
  isAllSection?: boolean;
}

const SortableSectionTypeItem: React.FC<SortableSectionTypeItemProps> = ({ sectionType, id, isAllSection = false }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: isAllSection,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 12px',
        border: '1px solid #e8e8e8',
        borderRadius: '8px',
        backgroundColor: isAllSection ? '#f5f5f5' : '#ffffff',
        cursor: isAllSection ? 'default' : 'grab',
        flexShrink: 0,
      }}
      {...listeners}
      {...attributes}>
      {!isAllSection && (
        <div style={{ cursor: 'grab', display: 'flex', alignItems: 'center' }}>
          <DragOutlined style={{ fontSize: '16px', color: '#999' }} />
        </div>
      )}
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 500, fontSize: '14px' }}>{sectionType}</div>
      </div>
    </div>
  );
};

interface SortableCollectionItemProps {
  item: Partial<ProductCollection> & { mainBannerImageUrlTemp?: string; miniBannerImageUrlTemp?: string };
  id: string;
  onChange: (item: ProductCollection) => void;
  onDeleteItem: () => void;
  onChangeImage: (name: 'mainBannerImageUrl' | 'miniBannerImageUrl', files: FileList) => void;
  isReadOnly?: boolean;
}

const SortableCollectionItem: React.FC<SortableCollectionItemProps> = ({
  item,
  id,
  onChange,
  onDeleteItem,
  onChangeImage,
  isReadOnly = false,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={{ ...style, flexShrink: 0, width: '320px' }} {...attributes}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          padding: '12px',
          border: '1px solid #f0f0f0',
          borderRadius: '8px',
          backgroundColor: '#ffffff',
        }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div {...listeners} style={{ cursor: 'grab' }}>
            <DragOutlined />
          </div>
          {!isReadOnly && <Button onClick={onDeleteItem} icon={<DeleteOutlined />} danger size="small" />}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              width: '287px',
              aspectRatio: '1.75',
              padding: '24px',
              background: `linear-gradient(180deg, rgba(0, 0, 0, 0.50) 0%, rgba(0, 0, 0, 0.00) 100%), url(${cdnImageUrl(
                item.mainBannerImageUrlTemp || item.mainBannerImageUrl,
              )}) no-repeat center center / cover`,
              backgroundSize: 'cover',
              borderRadius: '12px',
              overflow: 'hidden',
              boxSizing: 'border-box',
              cursor: 'pointer',
            }}>
            <div style={{ color: 'white', fontSize: '20px', fontWeight: 700, lineHeight: '26px' }}>
              <div>{item.mainCopy1}</div>
              <div>{item.mainCopy2}</div>
            </div>
            <div
              style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '20px',
              }}>
              {item.subCopy}
            </div>
          </div>
          {!isReadOnly && (
            <FileUploadButton
              onChange={(e) => e.target.files && onChangeImage('mainBannerImageUrl', e.target.files)}
              accept="image/*"
              style={{ width: '100%' }}>
              메인 배너 변경
            </FileUploadButton>
          )}
          <div
            style={{
              width: '100px',
              height: '60px',
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '12px',
              backgroundColor: '#f5f5f5',
            }}>
            <div
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                // backgroundColor: 'rgba(0, 0, 0, 0.2)',
                color: 'white',
                fontSize: '13px',
                fontWeight: 700,
              }}>
              {item.subTitle}
            </div>
            <img
              src={item.miniBannerImageUrlTemp || cdnImageUrl(item.miniBannerImageUrl)}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              alt="Mini Banner"
            />
          </div>
          {!isReadOnly && (
            <FileUploadButton
              onChange={(e) => e.target.files && onChangeImage('miniBannerImageUrl', e.target.files)}
              accept="image/*"
              style={{ width: '100px' }}>
              미니 배너 변경
            </FileUploadButton>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Form layout="vertical">
            <Form.Item label={<span style={{ fontWeight: 500 }}>기획전 제목</span>} style={{ marginBottom: '8px' }}>
              <Input value={item.title} disabled />
            </Form.Item>
            <Form.Item label={<span style={{ fontWeight: 500 }}>부제</span>} style={{ marginBottom: '8px' }}>
              <Input
                value={item.subTitle}
                onChange={(e) => onChange({ ...item, subTitle: e.target.value } as ProductCollection)}
                placeholder="부제"
                disabled={isReadOnly}
              />
            </Form.Item>
            <Form.Item label={<span style={{ fontWeight: 500 }}>메인카피 1</span>} style={{ marginBottom: '8px' }}>
              <Input
                value={item.mainCopy1}
                onChange={(e) => onChange({ ...item, mainCopy1: e.target.value } as ProductCollection)}
                placeholder="메인카피 1"
                disabled={isReadOnly}
              />
            </Form.Item>
            <Form.Item label={<span style={{ fontWeight: 500 }}>메인카피 2</span>} style={{ marginBottom: '8px' }}>
              <Input
                value={item.mainCopy2}
                onChange={(e) => onChange({ ...item, mainCopy2: e.target.value } as ProductCollection)}
                placeholder="메인카피 2"
                disabled={isReadOnly}
              />
            </Form.Item>
            <Form.Item label={<span style={{ fontWeight: 500 }}>서브카피</span>} style={{ marginBottom: '0' }}>
              <Input.TextArea
                value={item.subCopy}
                onChange={(e) => onChange({ ...item, subCopy: e.target.value } as ProductCollection)}
                placeholder="서브카피"
                rows={2}
                disabled={isReadOnly}
              />
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
};

const ProductCollectionRecommendEdit: React.FC<{
  onClose?: () => void;
}> = (props) => {
  const queryClient = useQueryClient();
  const dialog = useOkCancelDialog();
  const [feedData, setFeedData] = useState<Feed<FeedData<ProductCollection>> | null>(null);
  const [imageFiles, setImageFiles] = useState<{ [key: string]: File }>({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSectionType, setNewSectionType] = useState('');
  const [activeSectionType, setActiveSectionType] = useState('');

  const { data: initialFeedData } = useQuery({
    queryKey: ['product-collection-feed'],
    queryFn: () => feedApi.getProductCollectionFeed(),
    select: (res) => res.data,
  });

  const updateFeedMutation = useMutation({
    mutationFn: (variables: { data: FormData }) => feedApi.updateProductCollectionFeed(variables.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-collection-feed'] });
      dialog.open({ content: '저장되었습니다.', type: 'ok' });
    },
    onError: (err: any) => {
      console.error(err);
      dialog.open({ content: `저장에 실패했습니다: ${err.message}`, type: 'ok' });
    },
  });

  useEffect(() => {
    if (initialFeedData) {
      const data = initialFeedData as any;
      // "전체" 섹션이 항상 첫 번째에 오도록 보장
      if (data?.data) {
        const allSectionIndex = data.data.findIndex(
          (section: FeedData<ProductCollection>) => section.type === ALL_SECTION_TYPE,
        );
        if (allSectionIndex > 0) {
          const allSection = data.data[allSectionIndex];
          const newData = [...data.data];
          newData.splice(allSectionIndex, 1);
          newData.unshift(allSection);
          setFeedData({ ...data, data: newData });
        } else {
          setFeedData(data);
        }
      } else {
        setFeedData(data);
      }
    }
  }, [initialFeedData]);

  // 컴포넌트 언마운트 시 스크롤 복원
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleDragEnd = (type: string, event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id !== over?.id && feedData?.data) {
      const section = feedData.data.find((d) => d.type === type);
      if (!section?.data) return;
      const oldIndex = section.data.findIndex((item) => item._id === active.id);
      const newIndex = section.data.findIndex((item) => item._id === over?.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const newData = arrayMove(section.data, oldIndex, newIndex);
        setFeedData({
          ...feedData,
          data: feedData.data.map((d) => (d.type === type ? { ...d, data: newData } : d)),
        });
      }
    }
  };

  const handleSectionDragStart = () => {
    // 드래그 중 스크롤 방지
    document.body.style.overflow = 'hidden';
  };

  const handleSectionDragEnd = (event: DragEndEvent) => {
    // 드래그 종료 후 스크롤 복원
    document.body.style.overflow = '';

    const { active, over } = event;
    if (active.id !== over?.id && feedData?.data) {
      const activeSectionType = active.id as string;
      const overSectionType = over?.id as string;

      // "전체" 섹션을 드래그하려고 하면 무시
      if (activeSectionType === ALL_SECTION_TYPE) {
        return;
      }

      const oldIndex = feedData.data.findIndex((section) => section.type === activeSectionType);
      const newIndex = feedData.data.findIndex((section) => section.type === overSectionType);

      // "전체" 섹션 앞으로 이동하려고 하면 무시
      if (overSectionType === ALL_SECTION_TYPE && newIndex === 0) {
        return;
      }

      if (oldIndex !== -1 && newIndex !== -1) {
        const newData = arrayMove(feedData.data, oldIndex, newIndex);

        // "전체" 섹션이 첫 번째에 오도록 보장
        const allSectionIndex = newData.findIndex((section) => section.type === ALL_SECTION_TYPE);
        if (allSectionIndex !== 0 && allSectionIndex !== -1) {
          const allSection = newData[allSectionIndex];
          newData.splice(allSectionIndex, 1);
          newData.unshift(allSection);
        }

        setFeedData({
          ...feedData,
          data: newData,
        });
      }
    }
  };

  const handleChange = (sectionIndex: number, itemIndex: number, updatedItem: ProductCollection) => {
    if (feedData?.data) {
      const newData = [...feedData.data];
      const newSectionData = [...(newData[sectionIndex].data ?? [])];
      newSectionData[itemIndex] = updatedItem;
      newData[sectionIndex] = { ...newData[sectionIndex], data: newSectionData };
      setFeedData({ ...feedData, data: newData });
    }
  };

  const handleDelete = (sectionIndex: number, itemIndex: number) => {
    if (feedData?.data) {
      const currentSection = feedData.data[sectionIndex];
      const deletedItem = currentSection.data?.[itemIndex];
      if (!deletedItem) return;

      const newData = [...feedData.data];
      const newSectionData = [...(newData[sectionIndex].data ?? [])];
      newSectionData.splice(itemIndex, 1);
      newData[sectionIndex] = { ...newData[sectionIndex], data: newSectionData };

      // 다른 섹션에 동일한 기획전이 있는지 확인
      const remainsInOtherSections = newData.some(
        (section, idx) =>
          idx !== sectionIndex &&
          section.type !== ALL_SECTION_TYPE &&
          section.data?.some((item) => item._id === deletedItem._id),
      );

      // '전체' 섹션에서도 삭제 (다른 섹션에 남아있지 않으면)
      if (!remainsInOtherSections) {
        const allSectionIndex = newData.findIndex((d) => d.type === ALL_SECTION_TYPE);
        if (allSectionIndex !== -1) {
          const allSectionData = [...(newData[allSectionIndex].data ?? [])];
          const itemIndexInAll = allSectionData.findIndex((item) => item._id === deletedItem._id);
          if (itemIndexInAll !== -1) {
            allSectionData.splice(itemIndexInAll, 1);
            newData[allSectionIndex] = { ...newData[allSectionIndex], data: allSectionData };
          }
        }
      }

      setFeedData({ ...feedData, data: newData });
    }
  };

  const handleAddCollections = (type: string, collections: ProductCollection[]) => {
    if (feedData?.data) {
      const section = feedData.data.find((d) => d.type === type);
      if (!section) return;

      const existingIds = new Set((section.data ?? []).map((item) => item._id));
      const newCollections = collections
        .map((c) => ({
          _id: c._id,
          title: c.title,
          subTitle: c.subTitle,
          mainCopy1: c.mainCopy1,
          mainCopy2: c.mainCopy2,
          subCopy: c.subCopy,
          mainBannerImageUrl: c.mainBannerImageUrl,
          miniBannerImageUrl: c.miniBannerImageUrl,
        }))
        .filter((c) => !existingIds.has(c._id));
      const newData = [...(section.data ?? []), ...newCollections];

      // '전체' 섹션에도 추가 (중복 제외)
      const allSection = feedData.data.find((d) => d.type === ALL_SECTION_TYPE);
      const allExistingIds = new Set((allSection?.data ?? []).map((item) => item._id));
      const collectionsToAddToAll = newCollections.filter((c) => !allExistingIds.has(c._id));

      setFeedData({
        ...feedData,
        data: feedData.data.map((d) => {
          if (d.type === type) {
            return { ...d, data: newData as ProductCollection[] };
          }
          if (d.type === ALL_SECTION_TYPE && collectionsToAddToAll.length > 0) {
            return { ...d, data: [...(d.data ?? []), ...collectionsToAddToAll] as ProductCollection[] };
          }
          return d;
        }),
      });
    }
    setShowAddModal(false);
  };

  const handleImageChange = async (
    sectionIndex: number,
    itemIndex: number,
    name: 'mainBannerImageUrl' | 'miniBannerImageUrl',
    files: FileList,
  ) => {
    if (!files.length) return;
    const file = files[0];
    const { fileName } = extractFileName(file.name);
    const resized = await resizeImage(file, { maxWidth: 828 });
    const newFile = new File([resized.image], `${fileName}.${resized.type.split('/')[1]}`) as RcFile;

    const tempUrl = URL.createObjectURL(newFile);
    const uploadFileName = newFile.name;

    const section = feedData!.data![sectionIndex];
    if (!section?.data) return;
    const item = section.data[itemIndex];
    const updatedItem = {
      ...item,
      [name]: uploadFileName,
      [`${name}Temp`]: tempUrl,
    };
    handleChange(sectionIndex, itemIndex, updatedItem as ProductCollection);

    const imageKey = `${section.type}-${itemIndex}-${name}`;
    setImageFiles((prev) => ({ ...prev, [imageKey]: newFile }));
  };

  const handleSectionImageChange = async (sectionIndex: number, files: FileList) => {
    if (!files.length) return;
    const file = files[0];
    const { fileName } = extractFileName(file.name);
    const resized = await resizeImage(file, { maxWidth: 828 });
    const newFile = new File([resized.image], `${fileName}.${resized.type.split('/')[1]}`) as RcFile;

    const tempUrl = URL.createObjectURL(newFile);
    const uploadFileName = newFile.name;

    if (feedData?.data) {
      setFeedData({
        ...feedData,
        data: feedData.data.map((section, idx) =>
          idx === sectionIndex
            ? {
                ...section,
                imageUrl: uploadFileName,
                imageUrlTemp: tempUrl,
              }
            : section,
        ),
      });

      const imageKey = `section-${sectionIndex}-imageUrl`;
      setImageFiles((prev) => ({ ...prev, [imageKey]: newFile }));
    }
  };

  const handleSectionImageDelete = (sectionIndex: number) => {
    if (feedData?.data) {
      setFeedData({
        ...feedData,
        data: feedData.data.map((section, idx) =>
          idx === sectionIndex
            ? {
                ...section,
                imageUrl: undefined,
                imageUrlTemp: undefined,
              }
            : section,
        ),
      });

      // 이미지 파일도 제거
      const imageKey = `section-${sectionIndex}-imageUrl`;
      setImageFiles((prev) => {
        const newFiles = { ...prev };
        delete newFiles[imageKey];
        return newFiles;
      });
    }
  };

  const handleSave = () => {
    if (!feedData?.data) return;

    dialog.open({
      title: '저장',
      content: '변경 사항을 저장하시겠습니까?',
      onConfirm: () => {
        const formData = new FormData();
        const feedToSave = JSON.parse(JSON.stringify(feedData));

        // '전체' 섹션 자동 생성/업데이트
        const allSection = feedToSave.data.find((d: FeedData<ProductCollection>) => d.type === ALL_SECTION_TYPE);
        const otherSections = feedToSave.data.filter((d: FeedData<ProductCollection>) => d.type !== ALL_SECTION_TYPE);

        // 다른 섹션들의 모든 기획전 수집 (중복 제거)
        const allCollections = new Map<string, ProductCollection>();
        otherSections.forEach((section: FeedData<ProductCollection>) => {
          section.data?.forEach((item) => {
            if (!allCollections.has(item._id)) {
              allCollections.set(item._id, item);
            }
          });
        });

        // '전체' 섹션 생성 또는 업데이트
        const allCollectionsArray = Array.from(allCollections.values());
        let updatedAllSection: FeedData<ProductCollection>;

        if (allSection) {
          // 기존 '전체' 섹션의 순서 유지하되, 새로운 항목 추가 및 삭제된 항목 제거
          const existingOrder = allSection.data ?? [];
          const orderedCollections: ProductCollection[] = [];
          const addedIds = new Set<string>();

          // 기존 순서대로 유지
          existingOrder.forEach((item: ProductCollection) => {
            if (allCollections.has(item._id)) {
              orderedCollections.push(allCollections.get(item._id)!);
              addedIds.add(item._id);
            }
          });

          // 새로 추가된 항목들을 뒤에 추가
          allCollectionsArray.forEach((item) => {
            if (!addedIds.has(item._id)) {
              orderedCollections.push(item);
            }
          });

          updatedAllSection = {
            ...allSection,
            data: orderedCollections,
          };
        } else {
          // 새로운 '전체' 섹션 생성
          updatedAllSection = {
            type: ALL_SECTION_TYPE,
            data: allCollectionsArray,
            shuffle: false,
          };
        }

        // '전체' 섹션을 맨 앞에 배치
        feedToSave.data = [updatedAllSection, ...otherSections];

        const cleanedData = removeTempProperties(feedToSave);
        delete cleanedData.searchItems;

        Object.values(imageFiles).forEach((file) => {
          formData.append('files', file);
        });

        formData.append('json', JSON.stringify(cleanedData));
        updateFeedMutation.mutate({ data: formData });
      },
    });
  };

  const handleShuffleToggle = (type: string, checked: boolean) => {
    if (feedData?.data) {
      setFeedData({
        ...feedData,
        data: feedData.data.map((d) => (d.type === type ? { ...d, shuffle: checked } : d)),
      });
    }
  };

  const handleAddSection = () => {
    if (!newSectionType.trim()) return;
    const trimmedType = newSectionType.trim();

    if (trimmedType === ALL_SECTION_TYPE) {
      dialog.open({ content: `'${ALL_SECTION_TYPE}' 섹션은 자동으로 관리되므로 추가할 수 없습니다.`, type: 'ok' });
      return;
    }

    if (feedData?.data?.some((d) => d.type === trimmedType)) {
      dialog.open({ content: '이미 존재하는 섹션 타입입니다.', type: 'ok' });
      return;
    }

    const newSection: FeedData<ProductCollection> = {
      type: trimmedType,
      data: [],
      shuffle: false,
    };

    setFeedData((prev) => {
      return {
        ...prev,
        type: 'product-collection',
        data: [...(prev?.data ?? []), newSection],
      };
    });
    setNewSectionType('');
  };

  const handleDeleteSection = (type: string) => {
    if (type === ALL_SECTION_TYPE) {
      dialog.open({ content: `'${ALL_SECTION_TYPE}' 섹션은 삭제할 수 없습니다.`, type: 'ok' });
      return;
    }

    setFeedData((prev) => {
      if (!prev) return null;

      // 섹션 삭제 시 해당 섹션의 기획전들을 '전체' 섹션에서도 확인하여 삭제
      const newData = prev.data?.filter((d) => d.type !== type) ?? [];

      // 삭제되는 기획전들 중 다른 섹션에 남아있지 않은 것들을 찾기
      const remainingData = newData.filter((d) => d.type !== ALL_SECTION_TYPE);
      const remainingIds = new Set(remainingData.flatMap((section) => section.data?.map((item) => item._id) ?? []));

      // '전체' 섹션에서 삭제되는 항목들 제거
      return {
        ...prev,
        data: newData.map((d) => {
          if (d.type === ALL_SECTION_TYPE) {
            return {
              ...d,
              data: d.data?.filter((item) => remainingIds.has(item._id)),
            };
          }
          return d;
        }),
      };
    });
  };

  const handleTypeChange = (oldType: string, newType: string) => {
    setFeedData((prev) => {
      if (!prev) return null;

      return {
        ...prev,
        data: prev.data?.map((d) => (d.type === oldType ? { ...d, type: newType } : d)) ?? [],
      };
    });
  };

  return (
    <Modal
      open={true}
      onCancel={props.onClose}
      width="80%"
      centered
      styles={{ body: { height: '80vh', overflowY: 'auto' } }}
      title="신규 기획전 관리"
      footer={[
        <Button key="close" onClick={props.onClose}>
          닫기
        </Button>,
        <Button key="save" type="primary" onClick={handleSave} loading={updateFeedMutation.isPending}>
          저장
        </Button>,
      ]}>
      {showAddModal && (
        <SelectProductCollectionModal
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
          onAddCollections={(collections) => handleAddCollections(activeSectionType, collections)}
          existingIds={feedData?.data?.find((d) => d.type === activeSectionType)?.data?.map((item) => item._id) ?? []}
        />
      )}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: 600 }}>섹션 순서 관리</h3>
        <div style={{ padding: '16px', backgroundColor: '#fafafa', borderRadius: '8px', border: '1px solid #e8e8e8' }}>
          <DndContext
            sensors={useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 10 } }))}
            collisionDetection={closestCenter}
            onDragStart={handleSectionDragStart}
            onDragEnd={handleSectionDragEnd}>
            <SortableContext
              items={(feedData?.data ?? []).map((section) => section.type)}
              strategy={horizontalListSortingStrategy}>
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  paddingBottom: '8px',
                  touchAction: 'pan-x',
                }}>
                {(feedData?.data ?? []).map((section) => (
                  <SortableSectionTypeItem
                    key={section.type}
                    id={section.type}
                    sectionType={section.type}
                    itemCount={section.data?.length ?? 0}
                    isAllSection={section.type === ALL_SECTION_TYPE}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <Input
          placeholder="새 섹션 타입 입력 (예: 추천)"
          value={newSectionType}
          onChange={(e) => setNewSectionType(e.target.value)}
          onPressEnter={handleAddSection}
        />
        <Button onClick={handleAddSection} type="primary">
          섹션 추가
        </Button>
      </div>

      <div className="flex-grow overflow-auto p-3">
        {(feedData?.data ?? []).map((section, sectionIndex) => (
          <SectionEditor
            key={section.type}
            section={section}
            onShuffleToggle={(checked) => handleShuffleToggle(section.type, checked)}
            onAddClick={() => {
              setActiveSectionType(section.type);
              setShowAddModal(true);
            }}
            onDeleteSection={() => handleDeleteSection(section.type)}
            onDragEnd={(event) => handleDragEnd(section.type, event)}
            onItemChange={(itemIndex, updatedItem) => handleChange(sectionIndex, itemIndex, updatedItem)}
            onItemDelete={(itemIndex) => handleDelete(sectionIndex, itemIndex)}
            onItemImageChange={(itemIndex, name, files) => handleImageChange(sectionIndex, itemIndex, name, files)}
            onTypeChange={handleTypeChange}
            onBadgeChange={(badge) => {
              if (feedData?.data) {
                setFeedData({
                  ...feedData,
                  data: feedData.data.map((d, idx) => (idx === sectionIndex ? { ...d, badge } : d)),
                });
              }
            }}
            onSectionImageChange={(files) => handleSectionImageChange(sectionIndex, files)}
            onSectionImageDelete={() => handleSectionImageDelete(sectionIndex)}
            allSectionTypes={(feedData?.data ?? []).map((s) => s.type).filter((t) => t !== section.type)}
          />
        ))}
      </div>
    </Modal>
  );
};

interface SectionEditorProps {
  section: FeedData<ProductCollection>;
  onShuffleToggle: (checked: boolean) => void;
  onAddClick: () => void;
  onDeleteSection: () => void;
  onDragEnd: (event: DragEndEvent) => void;
  onItemChange: (itemIndex: number, updatedItem: ProductCollection) => void;
  onItemDelete: (itemIndex: number) => void;
  onItemImageChange: (itemIndex: number, name: 'mainBannerImageUrl' | 'miniBannerImageUrl', files: FileList) => void;
  onTypeChange: (oldType: string, newType: string) => void;
  allSectionTypes: string[];
  onBadgeChange: (badge?: { text: string; textColor: string; color: string }) => void;
  onSectionImageChange: (files: FileList) => void;
  onSectionImageDelete: () => void;
}

const SectionEditor: React.FC<SectionEditorProps> = ({
  section,
  onShuffleToggle,
  onAddClick,
  onDeleteSection,
  onDragEnd,
  onItemChange,
  onItemDelete,
  onItemImageChange,
  onTypeChange,
  allSectionTypes,
  onBadgeChange,
  onSectionImageChange,
  onSectionImageDelete,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isEditingType, setIsEditingType] = useState(false);
  const [editedType, setEditedType] = useState(section.type);
  const [badgeEnabled, setBadgeEnabled] = useState(!!section.badge);
  const [badgeText, setBadgeText] = useState(section.badge?.text || '');
  const [badgeTextColor, setBadgeTextColor] = useState(section.badge?.textColor || '#ffffff');
  const [badgeColor, setBadgeColor] = useState(section.badge?.color || '#ff4d4f');
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 10 } }));
  const isAllSection = section.type === ALL_SECTION_TYPE;

  // 섹션의 뱃지 정보가 변경되면 로컬 상태 업데이트
  useEffect(() => {
    setBadgeEnabled(!!section.badge);
    setBadgeText(section.badge?.text || '');
    setBadgeTextColor(section.badge?.textColor || '#ffffff');
    setBadgeColor(section.badge?.color || '#ff4d4f');
  }, [section.badge]);

  const handleBadgeToggle = (checked: boolean) => {
    setBadgeEnabled(checked);
    if (checked) {
      onBadgeChange({
        text: badgeText,
        textColor: badgeTextColor,
        color: badgeColor,
      });
    } else {
      onBadgeChange(undefined);
    }
  };

  const handleBadgeTextChange = (text: string) => {
    setBadgeText(text);
    if (badgeEnabled) {
      onBadgeChange({
        text,
        textColor: badgeTextColor,
        color: badgeColor,
      });
    }
  };

  const handleBadgeTextColorChange = (color: string) => {
    setBadgeTextColor(color);
    if (badgeEnabled) {
      onBadgeChange({
        text: badgeText,
        textColor: color,
        color: badgeColor,
      });
    }
  };

  const handleBadgeColorChange = (color: string) => {
    setBadgeColor(color);
    if (badgeEnabled) {
      onBadgeChange({
        text: badgeText,
        textColor: badgeTextColor,
        color,
      });
    }
  };

  const handleTypeChange = () => {
    const trimmedType = editedType.trim();
    if (!trimmedType || trimmedType === section.type) {
      setIsEditingType(false);
      setEditedType(section.type);
      return;
    }

    if (trimmedType === ALL_SECTION_TYPE) {
      alert(`'${ALL_SECTION_TYPE}' 섹션명은 예약어로 사용할 수 없습니다.`);
      setEditedType(section.type);
      setIsEditingType(false);
      return;
    }

    if (allSectionTypes.includes(trimmedType)) {
      alert('이미 존재하는 섹션 타입입니다.');
      setEditedType(section.type);
      setIsEditingType(false);
      return;
    }

    onTypeChange(section.type, trimmedType);
    setIsEditingType(false);
  };

  return (
    <div style={{ marginBottom: '24px', border: '1px solid #e8e8e8', borderRadius: '8px', overflow: 'hidden' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#fafafa',
          padding: '12px 16px',
          cursor: 'pointer',
          borderBottom: isCollapsed ? 'none' : '1px solid #e8e8e8',
        }}
        onClick={() => setIsCollapsed(!isCollapsed)}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          <h2
            style={{ fontSize: '18px', fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            섹션:
            {isEditingType ? (
              <Input
                value={editedType}
                onChange={(e) => setEditedType(e.target.value)}
                onBlur={handleTypeChange}
                onPressEnter={handleTypeChange}
                onClick={(e) => e.stopPropagation()}
                style={{ width: '200px' }}
                autoFocus
                disabled={isAllSection}
              />
            ) : (
              <span
                onClick={(e) => {
                  if (!isAllSection) {
                    e.stopPropagation();
                    setIsEditingType(true);
                  }
                }}
                style={{
                  cursor: isAllSection ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}>
                {section.type}
                {!isAllSection && (
                  <EditOutlined
                    style={{
                      fontSize: '14px',
                      color: '#1890ff',
                    }}
                  />
                )}
              </span>
            )}
            <span style={{ color: '#888', fontSize: '14px', fontWeight: 400 }}>
              ({section.data?.length ?? 0}개 항목)
            </span>
            {isAllSection && (
              <span style={{ color: '#1890ff', fontSize: '12px', fontWeight: 400, marginLeft: '8px' }}>
                (자동 관리 - 순서만 변경 가능)
              </span>
            )}
          </h2>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {!isAllSection && (
            <Button
              icon={<DeleteOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onDeleteSection();
              }}
              danger
              size="small">
              섹션 삭제
            </Button>
          )}
          {isCollapsed ? <CaretDownOutlined /> : <CaretUpOutlined />}
        </div>
      </div>
      {!isCollapsed && (
        <div style={{ padding: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontWeight: 500 }}>무작위 노출: </span>
              <Switch checked={!!section.shuffle} onChange={onShuffleToggle} />
            </div>
            {!isAllSection && (
              <Button onClick={onAddClick} type="primary">
                기획전 추가
              </Button>
            )}
          </div>
          {!isAllSection && (
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              {/* 오른쪽: 섹션 이미지 (50%) */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <span style={{ fontWeight: 500, fontSize: '14px' }}>섹션 미니배너</span>
                <span style={{ fontWeight: 500, fontSize: '12px', color: 'red' }}>
                  *배너설정시 전체탭에 미니배너 노출, 하위 기획전 미니배너들은 미노출됨. <br />
                  배너클릭시 해당 탭으로 이동.
                </span>
                <div
                  style={{
                    width: '100px',
                    height: '60px',
                    borderRadius: '8px',
                    backgroundColor: '#f5f5f5',
                    border: '1px solid #e8e8e8',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}>
                  {section.imageUrlTemp || section.imageUrl ? (
                    <img
                      src={cdnImageUrl(section.imageUrlTemp || section.imageUrl)}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      alt="Section"
                    />
                  ) : (
                    <span style={{ color: '#999', fontSize: '12px' }}>이미지 없음</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <FileUploadButton
                    onChange={(e) => e.target.files && onSectionImageChange(e.target.files)}
                    accept="image/*"
                    style={{ width: '100px' }}>
                    이미지 변경
                  </FileUploadButton>
                  {(section.imageUrlTemp || section.imageUrl) && (
                    <Button danger size="small" onClick={onSectionImageDelete} icon={<DeleteOutlined />}>
                      삭제
                    </Button>
                  )}
                </div>
              </div>
              {/* 왼쪽: 뱃지 설정 (50%) */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Checkbox checked={badgeEnabled} onChange={(e) => handleBadgeToggle(e.target.checked)}>
                    뱃지 설정
                  </Checkbox>
                </div>
                {badgeEnabled && (
                  <div
                    style={{
                      padding: '12px',
                      border: '1px solid #e8e8e8',
                      borderRadius: '8px',
                      backgroundColor: '#fafafa',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                    }}>
                    <Form.Item label={<span style={{ fontWeight: 500 }}>뱃지 텍스트</span>} style={{ marginBottom: 0 }}>
                      <Input
                        value={badgeText}
                        onChange={(e) => handleBadgeTextChange(e.target.value)}
                        placeholder="뱃지 텍스트를 입력하세요"
                      />
                    </Form.Item>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <Form.Item
                        label={<span style={{ fontWeight: 500 }}>텍스트 색상</span>}
                        style={{ marginBottom: 0, flex: 1 }}>
                        <ColorPicker
                          value={badgeTextColor}
                          onChange={(color) => handleBadgeTextColorChange(color.toHexString())}
                          showText
                          size="small"
                        />
                      </Form.Item>
                      <Form.Item
                        label={<span style={{ fontWeight: 500 }}>배경 색상</span>}
                        style={{ marginBottom: 0, flex: 1 }}>
                        <ColorPicker
                          value={badgeColor}
                          onChange={(color) => handleBadgeColorChange(color.toHexString())}
                          showText
                          size="small"
                        />
                      </Form.Item>
                    </div>
                    {badgeText && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '12px', color: '#666' }}>미리보기:</span>
                        <div
                          style={{
                            display: 'inline-block',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            backgroundColor: badgeColor,
                            color: badgeTextColor,
                            fontSize: '12px',
                            fontWeight: 500,
                          }}>
                          {badgeText}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext
              items={section.data?.map((item) => item._id) ?? []}
              strategy={horizontalListSortingStrategy}>
              <div style={{ display: 'flex', gap: '16px', overflowX: 'auto', paddingBottom: '16px' }}>
                {(section.data ?? []).map((item, index) => (
                  <SortableCollectionItem
                    key={item._id}
                    id={item._id}
                    item={item}
                    onChange={(updatedItem) => onItemChange(index, updatedItem)}
                    onDeleteItem={() => onItemDelete(index)}
                    onChangeImage={(name, files) => onItemImageChange(index, name, files)}
                    isReadOnly={isAllSection}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
};

export default ProductCollectionRecommendEdit;
